import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { selectUserAttributes } from '@features/auth/slice';
import { useIsVendor } from './useIsVendor';

interface OwnablePlugin {
  type?: string;
  ownerId?: string;
}

/**
 * Returns a predicate answering "does the logged-in vendor own this plugin?" —
 * a client-side mirror of integration-service's `ValidateVendorPluginAccess`:
 * a non-admin vendor may only reach a `Vendor`-type plugin whose non-empty
 * `OwnerId` equals their own entity id.
 *
 * **This is not a security boundary.** The server enforces the real check on
 * every request; this only decides whether a vendor-owned capability is worth
 * offering in the UI. Admins carry no entity id and are handled by the caller,
 * not here — this predicate is only meaningful for a vendor caller.
 *
 * A non-empty `ownerId` is required so admin-created plugins (`OwnerId` empty)
 * never match, matching the server rule exactly.
 */
export function useIsVendorPluginOwner(): (plugin?: OwnablePlugin) => boolean {
  const attrs = useSelector(selectUserAttributes);
  const isVendor = useIsVendor();

  return useCallback(
    (plugin?: OwnablePlugin): boolean => {
      if (!isVendor || !plugin) return false;
      if (plugin.type !== 'Vendor') return false;
      if (!plugin.ownerId) return false;
      const own = attrs.entityId ?? [];
      return own.includes(plugin.ownerId);
    },
    [attrs, isVendor],
  );
}
